import {
    LayoutDashboard,
    Users,
    Settings,
    LogOut,
    HeartPulse,
    FileText,
    Calculator,
    LifeBuoy,
    Send
} from "lucide-react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { useAuthStore } from "../../stores/authStore"
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"

export function AppSidebar() {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout } = useAuthStore()

    const isActive = (path: string) => location.pathname.startsWith(path)

    const handleLogout = () => {
        logout()
        navigate("/login")
    }

    const navMain = [
        {
            title: "Platform",
            items: [
                {
                    title: "Dashboard",
                    url: "/dashboard",
                    icon: LayoutDashboard,
                },
                {
                    title: "Patients",
                    url: "/patients",
                    icon: Users,
                },
                {
                    title: "Assessments",
                    url: "/assessments",
                    icon: Calculator,
                },
                {
                    title: "Reports",
                    url: "/reports",
                    icon: FileText,
                },
            ],
        },
        {
            title: "Settings",
            items: [
                {
                    title: "General",
                    url: "/settings",
                    icon: Settings,
                },
                {
                    title: "Support",
                    url: "/support",
                    icon: LifeBuoy,
                },
                {
                    title: "Feedback",
                    url: "/feedback",
                    icon: Send,
                },
            ],
        },
    ]

    return (
        <Sidebar collapsible="icon">
            <SidebarHeader>
                <div className="flex items-center gap-2 py-2">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-blue-600 text-white">
                        <HeartPulse className="h-5 w-5" />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                        <span className="truncate font-semibold">Narayana Health</span>
                        <span className="truncate text-xs">Risk Assessment</span>
                    </div>
                </div>
            </SidebarHeader>
            <SidebarContent>
                {navMain.map((group) => (
                    <SidebarGroup key={group.title}>
                        <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {group.items.map((item) => (
                                    <SidebarMenuItem key={item.title}>
                                        <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                                            <Link to={item.url}>
                                                <item.icon />
                                                <span>{item.title}</span>
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                ))}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                ))}
            </SidebarContent>
            <SidebarFooter>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            size="lg"
                            onClick={handleLogout}
                            className="cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        >
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarImage alt={user?.username} />
                                <AvatarFallback className="rounded-lg">
                                    {user?.username?.slice(0, 2).toUpperCase() || "DR"}
                                </AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                                <span className="truncate font-semibold">{user?.username || "Doctor"}</span>
                                <span className="truncate text-xs">{user?.email || "doctor@hospital.com"}</span>
                            </div>
                            <LogOut className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    )
}
